import React, { useState, useEffect } from 'react';
import {
    FaCalendarAlt, FaMapMarkerAlt, FaClock, FaUsers, FaChartLine,
    FaFilter, FaSearch, FaStar, FaCheckCircle, FaArrowRight,
    FaGraduationCap, FaBookOpen, FaLaptopCode, FaUserTie,
    FaFire, FaTags, FaRegClock, FaChevronDown, FaExclamationTriangle,
    FaSpinner
} from 'react-icons/fa';
import { Link } from 'react-router';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { API_URL, resolveImageUrl } from '../config/api';

const DowSession = () => {
    const [sessions,         setSessions]         = useState([]);
    const [filteredSessions, setFilteredSessions] = useState([]);
    const [loading,          setLoading]          = useState(true);
    const [error,            setError]            = useState(null);
    const [searchTerm,       setSearchTerm]       = useState('');
    const [selectedDomain,   setSelectedDomain]   = useState('all');
    const [selectedCity,     setSelectedCity]     = useState('all');
    const [selectedType,     setSelectedType]     = useState('all');

    // ── Fetch depuis l'API (sessions actives uniquement) ─────────────────────
    useEffect(() => {
        setLoading(true);
        setError(null);

        fetch(`${API_URL}/formations/sessions/public`)
            .then(res => {
                if (!res.ok) {
                    if (res.status === 500) {
                        throw new Error('Le serveur backend rencontre un problème. Vérifiez que toutes les colonnes de la base de données sont présentes (is_active, is_popular, is_new_session, rating, type).');
                    }
                    throw new Error(`Erreur ${res.status}`);
                }
                return res.json();
            })
            .then(data => {
                const list = data.data || data || [];
                setSessions(list);
                setFilteredSessions(list);
            })
            .catch(err => {
                console.error('Erreur fetch sessions:', err);
                setError(err.message || 'Impossible de charger les formations. Veuillez réessayer.');
            })
            .finally(() => setLoading(false));
    }, []);

    // ── Filtrage ──────────────────────────────────────────────────────────────
    useEffect(() => {
        let result = sessions;

        if (searchTerm) {
            result = result.filter(session =>
                session.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                session.domain?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        if (selectedDomain !== 'all') result = result.filter(s => s.domain === selectedDomain);
        if (selectedCity   !== 'all') result = result.filter(s => s.city   === selectedCity);
        if (selectedType   !== 'all') result = result.filter(s => s.type   === selectedType);

        setFilteredSessions(result);
    }, [searchTerm, selectedDomain, selectedCity, selectedType, sessions]);

    const domains = [...new Set(sessions.map(s => s.domain).filter(Boolean))];
    const cities  = [...new Set(sessions.map(s => s.city).filter(Boolean))];
    const types   = [...new Set(sessions.map(s => s.type).filter(Boolean))];

    const stats = [
        { label: 'Sessions Actives',    value: sessions.length,                                        icon: FaCalendarAlt, color: '#fd7e14' },
        { label: 'Places Disponibles',  value: sessions.reduce((acc, s) => acc + (s.seatsAvailable || 0), 0), icon: FaUsers,       color: '#198754' },
        { label: 'Domaines',            value: domains.length,                                         icon: FaBookOpen,    color: '#20c997' },
        { label: 'Villes',              value: cities.length,                                          icon: FaMapMarkerAlt, color: '#dc3545' },
    ];

    const formatDate = (dateString) => {
        if (!dateString) return '—';
        const options = { day: '2-digit', month: 'short', year: 'numeric' };
        return new Date(dateString).toLocaleDateString('fr-FR', options);
    };

    const getOccupancyPercentage = (session) => {
        if (!session.seats || session.seats === 0) return 0;
        return ((session.seats - (session.seatsAvailable || 0)) / session.seats) * 100;
    };

    const getOccupancyStatus = (percentage) => {
        if (percentage >= 80) return { label: 'Presque complet',    color: '#dc3545' };
        if (percentage >= 50) return { label: 'Places limitées',    color: '#fd7e14' };
        return                       { label: 'Places disponibles', color: '#198754' };
    };

    return (
        <>
        <Header />
        <div className="sessions-wrapper">
            {/* Hero Section */}
            <section className="sessions-hero">
                <div className="hero-overlay"></div>
                <div className="sessions-container">
                    <div className="hero-content">
                        <div className="hero-badge">
                            <FaFire className="badge-icon-pulse" />
                            <span>Inscrivez-vous Maintenant</span>
                        </div>
                        <h1 className="hero-title">
                            Sessions de formation <br />
                            <span className="gradient-text">en cours</span>
                        </h1>
                        <p className="hero-description">
                            Découvrez nos formations professionnelles disponibles et inscrivez-vous
                            dès aujourd'hui pour développer vos compétences avec les meilleurs experts.
                        </p>
                        <div className="hero-search">
                            <FaSearch className="search-icon" />
                            <input
                                type="text"
                                placeholder="Rechercher une formation par nom ou domaine..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="search-input"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="stats-section">
                <div className="sessions-container">
                    <div className="stats-grid">
                        {stats.map((stat, index) => {
                            const Icon = stat.icon;
                            return (
                                <div key={index} className="stat-card" style={{ '--stat-color': stat.color }}>
                                    <div className="stat-icon"><Icon /></div>
                                    <div className="stat-content">
                                        <div className="stat-value">
                                            {loading ? '…' : stat.value}
                                        </div>
                                        <div className="stat-label">{stat.label}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Filters & Sessions */}
            <section className="sessions-section">
                <div className="sessions-container">
                    {/* Filters Bar */}
                    <div className="filters-bar">
                        <div className="filters-left">
                            <div className="filter-group">
                                <FaFilter className="filter-icon" />
                                <select value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)} className="filter-select">
                                    <option value="all">Tous les domaines</option>
                                    {domains.map(domain => <option key={domain} value={domain}>{domain}</option>)}
                                </select>
                            </div>
                            <div className="filter-group">
                                <FaMapMarkerAlt className="filter-icon" />
                                <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} className="filter-select">
                                    <option value="all">Toutes les villes</option>
                                    {cities.map(city => <option key={city} value={city}>{city}</option>)}
                                </select>
                            </div>
                            <div className="filter-group">
                                <FaLaptopCode className="filter-icon" />
                                <select value={selectedType} onChange={(e) => setSelectedType(e.target.value)} className="filter-select">
                                    <option value="all">Tous les types</option>
                                    {types.map(type => <option key={type} value={type}>{type}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="filters-right">
                            <div className="results-count">
                                {loading ? 'Chargement…' : `${filteredSessions.length} formation${filteredSessions.length > 1 ? 's' : ''} trouvée${filteredSessions.length > 1 ? 's' : ''}`}
                            </div>
                        </div>
                    </div>

                    {/* État de chargement */}
                    {loading && (
                        <div className="loading-state">
                            <FaSpinner className="spin-icon" />
                            <p>Chargement des formations en cours…</p>
                        </div>
                    )}

                    {/* État d'erreur */}
                    {!loading && error && (
                        <div className="error-state">
                            <FaExclamationTriangle className="error-icon" />
                            <h3>Une erreur est survenue</h3>
                            <p>{error}</p>
                            <button onClick={() => window.location.reload()} className="btn-reset">
                                Réessayer
                            </button>
                        </div>
                    )}

                    {/* Sessions Grid */}
                    {!loading && !error && (
                        filteredSessions.length > 0 ? (
                            <div className="sessions-grid">
                                {filteredSessions.map((session, index) => (
                                    <div key={session.id} className="session-card" style={{ animationDelay: `${index * 0.08}s` }}>
                                        {/* Image */}
                                        <div className="session-image-wrapper">
                                            {session.image ? (
                                                <img src={resolveImageUrl(session.image)} alt={session.title} className="session-image" />
                                            ) : (
                                                <div className="session-image-placeholder">
                                                    <FaGraduationCap />
                                                </div>
                                            )}
                                            <div className="session-overlay"></div>
                                            <div className="session-badges">
                                                {session.isNewSession && (
                                                    <div className="badge badge-new"><FaFire /> Nouveau</div>
                                                )}
                                                {session.isPopular && (
                                                    <div className="badge badge-popular"><FaStar /> Populaire</div>
                                                )}
                                            </div>
                                            <div className="type-badge">{session.type}</div>
                                        </div>

                                        {/* Content */}
                                        <div className="session-content">
                                            <div className="domain-tag">
                                                <FaTags />{session.domain}
                                            </div>
                                            <h3 className="session-title">{session.title}</h3>
                                            {session.instructor && (
                                                <div className="session-instructor">
                                                    <FaUserTie /><span>{session.instructor}</span>
                                                </div>
                                            )}
                                            <div className="session-info">
                                                <div className="info-item">
                                                    <FaCalendarAlt />
                                                    <div>
                                                        <div className="info-label">Début</div>
                                                        <div className="info-value">{formatDate(session.startDate)}</div>
                                                    </div>
                                                </div>
                                                <div className="info-item">
                                                    <FaRegClock />
                                                    <div>
                                                        <div className="info-label">Durée</div>
                                                        <div className="info-value">{session.duration || '—'}</div>
                                                    </div>
                                                </div>
                                                <div className="info-item">
                                                    <FaMapMarkerAlt />
                                                    <div>
                                                        <div className="info-label">Ville</div>
                                                        <div className="info-value">{session.city || '—'}</div>
                                                    </div>
                                                </div>
                                                <div className="info-item">
                                                    <FaChartLine />
                                                    <div>
                                                        <div className="info-label">Niveau</div>
                                                        <div className="info-value">{session.level || '—'}</div>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Seats Progress */}
                                            {session.seats > 0 && (
                                                <div className="seats-info">
                                                    <div className="seats-header">
                                                        <span className="seats-text">
                                                            <FaUsers /> {session.seatsAvailable} places restantes sur {session.seats}
                                                        </span>
                                                        <span className="seats-status" style={{ color: getOccupancyStatus(getOccupancyPercentage(session)).color }}>
                                                            {getOccupancyStatus(getOccupancyPercentage(session)).label}
                                                        </span>
                                                    </div>
                                                    <div className="seats-progress">
                                                        <div
                                                            className="seats-progress-bar"
                                                            style={{
                                                                width: `${getOccupancyPercentage(session)}%`,
                                                                background: getOccupancyStatus(getOccupancyPercentage(session)).color
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            )}

                                            {/* Footer */}
                                            <div className="session-footer">
                                                <div className="session-price">
                                                    <div className="price-label">Prix</div>
                                                    <div className="price-value">{session.price}</div>
                                                </div>
                                                <Link
                                                    to="/inscriptionformation"
                                                    state={{ session: { id: session.id, title: session.title, domain: session.domain, price: session.price, startDate: session.startDate, city: session.city, image: session.image, duration: session.duration, seatsAvailable: session.seatsAvailable } }}
                                                    className="btn-inscription"
                                                >
                                                    S'inscrire
                                                </Link>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="no-results">
                                <FaSearch className="no-results-icon" />
                                <h3>Aucune session trouvée</h3>
                                <p>Essayez de modifier vos critères de recherche</p>
                                <button
                                    onClick={() => {
                                        setSearchTerm('');
                                        setSelectedDomain('all');
                                        setSelectedCity('all');
                                        setSelectedType('all');
                                    }}
                                    className="btn-reset"
                                >
                                    Réinitialiser les filtres
                                </button>
                            </div>
                        )
                    )}
                </div>
            </section>

            <style>{styles}</style>
        </div>
        <Footer />
        </>
    );
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');
  * { margin:0; padding:0; box-sizing:border-box }

  .sessions-wrapper { font-family:'Inter',sans-serif; background:#fafafa }
  .sessions-container { max-width:1280px; margin:0 auto; padding:0 24px }

  /* Hero */
  .sessions-hero { min-height:50vh; display:flex; align-items:center; background:linear-gradient(135deg,#198754 0%,#20c997 100%); padding:120px 0 80px; position:relative; overflow:hidden }
  .hero-overlay { position:absolute; top:0; left:0; right:0; bottom:0; background:url('https://images.unsplash.com/photo-1524178232363-1fb2b075b655?w=1600&q=80') center/cover; opacity:0.1 }
  .hero-content { text-align:center; position:relative; z-index:1; max-width:900px; margin:0 auto }
  .hero-badge { display:inline-flex; align-items:center; gap:10px; background:rgba(255,255,255,0.15); backdrop-filter:blur(10px); padding:10px 24px; border-radius:50px; margin-bottom:24px; border:1px solid rgba(255,255,255,0.2); color:white; font-weight:600; font-size:14px }
  .badge-icon-pulse { animation:pulse 2s ease-in-out infinite }
  @keyframes pulse { 0%,100%{transform:scale(1);opacity:1} 50%{transform:scale(1.2);opacity:0.8} }
  .hero-title { font-size:52px; font-weight:900; line-height:1.1; color:white; margin-bottom:20px }
  .gradient-text { background:linear-gradient(135deg,rgba(255,255,255,0.9) 0%,rgba(255,255,255,1) 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text }
  .hero-description { font-size:18px; line-height:1.7; color:rgba(255,255,255,0.95); margin-bottom:40px; max-width:700px; margin-left:auto; margin-right:auto }
  .hero-search { max-width:600px; margin:0 auto; position:relative }
  .search-icon { position:absolute; left:20px; top:50%; transform:translateY(-50%); color:#6b7280; font-size:20px }
  .search-input { width:100%; padding:18px 20px 18px 56px; border:none; border-radius:16px; font-size:16px; font-family:inherit; background:white; box-shadow:0 12px 40px rgba(0,0,0,0.15); transition:all 0.3s ease }
  .search-input:focus { outline:none; box-shadow:0 16px 50px rgba(0,0,0,0.2) }
  .search-input::placeholder { color:#9ca3af }

  /* Stats */
  .stats-section { padding:60px 0; background:white; margin-top:-40px; position:relative; z-index:2 }
  .stats-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:24px }
  .stat-card { background:white; border-radius:20px; padding:32px 24px; box-shadow:0 4px 20px rgba(0,0,0,0.08); display:flex; align-items:center; gap:20px; transition:all 0.3s ease; border:2px solid transparent }
  .stat-card:hover { transform:translateY(-8px); box-shadow:0 12px 32px rgba(0,0,0,0.12); border-color:var(--stat-color) }
  .stat-icon { width:60px; height:60px; border-radius:16px; background:var(--stat-color); display:flex; align-items:center; justify-content:center; color:white; font-size:24px; flex-shrink:0 }
  .stat-value { font-size:32px; font-weight:900; color:#1a202c; line-height:1; margin-bottom:6px }
  .stat-label { font-size:14px; color:#6b7280; font-weight:600 }

  /* Sessions section */
  .sessions-section { padding:60px 0 100px; background:#fafafa }

  /* Filters */
  .filters-bar { display:flex; justify-content:space-between; align-items:center; margin-bottom:40px; padding:24px; background:white; border-radius:16px; box-shadow:0 2px 12px rgba(0,0,0,0.06); flex-wrap:wrap; gap:16px }
  .filters-left { display:flex; gap:16px; flex-wrap:wrap; flex:1 }
  .filter-group { display:flex; align-items:center; gap:10px; background:#f9fafb; padding:10px 16px; border-radius:12px; border:2px solid #e5e7eb; transition:all 0.3s ease }
  .filter-group:focus-within { border-color:#198754; background:white }
  .filter-icon { color:#198754; font-size:16px }
  .filter-select { border:none; background:transparent; font-size:14px; font-weight:600; color:#374151; cursor:pointer; font-family:inherit; outline:none; min-width:150px }
  .results-count { font-size:15px; font-weight:700; color:#1a202c; background:#f0fdf4; padding:10px 20px; border-radius:10px; border:2px solid #198754 }

  /* Loading state */
  .loading-state { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:80px 32px; color:#9ca3af }
  .spin-icon { font-size:40px; margin-bottom:16px; animation:spin 1s linear infinite; color:#198754 }
  @keyframes spin { to{transform:rotate(360deg)} }
  .loading-state p { font-size:16px; font-weight:500 }

  /* Error state */
  .error-state { text-align:center; padding:80px 32px; background:white; border-radius:24px; box-shadow:0 4px 20px rgba(0,0,0,0.06) }
  .error-icon { font-size:56px; color:#fd7e14; margin-bottom:20px }
  .error-state h3 { font-size:24px; font-weight:700; color:#374151; margin-bottom:10px }
  .error-state p { font-size:15px; color:#6b7280; margin-bottom:28px }

  /* Image placeholder */
  .session-image-placeholder { width:100%; height:100%; background:linear-gradient(135deg,#198754,#20c997); display:flex; align-items:center; justify-content:center; font-size:56px; color:rgba(255,255,255,0.3) }

  /* Sessions Grid */
  .sessions-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(380px,1fr)); gap:32px }
  .session-card { background:white; border-radius:24px; overflow:hidden; box-shadow:0 4px 20px rgba(0,0,0,0.08); transition:all 0.4s cubic-bezier(0.4,0,0.2,1); border:2px solid transparent; animation:fadeInUp 0.6s ease-out backwards }
  @keyframes fadeInUp { from{opacity:0;transform:translateY(30px)} to{opacity:1;transform:translateY(0)} }
  .session-card:hover { transform:translateY(-12px); box-shadow:0 20px 60px rgba(0,0,0,0.15); border-color:#198754 }
  .session-image-wrapper { position:relative; height:200px; overflow:hidden }
  .session-image { width:100%; height:100%; object-fit:cover; transition:transform 0.6s ease }
  .session-card:hover .session-image { transform:scale(1.1) }
  .session-overlay { position:absolute; top:0; left:0; right:0; bottom:0; background:linear-gradient(180deg,transparent 0%,rgba(0,0,0,0.6) 100%) }
  .session-badges { position:absolute; top:16px; left:16px; display:flex; gap:8px; z-index:2 }
  .badge { display:flex; align-items:center; gap:6px; padding:6px 14px; border-radius:8px; font-size:12px; font-weight:700; backdrop-filter:blur(10px) }
  .badge-new { background:rgba(253,126,20,0.95); color:white }
  .badge-popular { background:rgba(255,193,7,0.95); color:#1a202c }
  .type-badge { position:absolute; bottom:16px; right:16px; background:rgba(255,255,255,0.95); backdrop-filter:blur(10px); padding:8px 16px; border-radius:8px; font-size:13px; font-weight:700; color:#198754; z-index:2 }
  .session-content { padding:28px }
  .domain-tag { display:inline-flex; align-items:center; gap:6px; background:#f0fdf4; color:#198754; padding:6px 14px; border-radius:8px; font-size:12px; font-weight:700; margin-bottom:16px }
  .domain-tag svg { font-size:10px }
  .session-title { font-size:20px; font-weight:800; color:#1a202c; margin-bottom:12px; line-height:1.3; display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden }
  .session-instructor { display:flex; align-items:center; gap:8px; font-size:14px; color:#6b7280; margin-bottom:20px; font-weight:500 }
  .session-instructor svg { color:#fd7e14; font-size:14px }
  .session-info { display:grid; grid-template-columns:repeat(2,1fr); gap:16px; margin-bottom:20px; padding:20px; background:#f9fafb; border-radius:12px }
  .info-item { display:flex; align-items:flex-start; gap:10px }
  .info-item svg { color:#198754; font-size:16px; margin-top:2px; flex-shrink:0 }
  .info-label { font-size:11px; color:#9ca3af; font-weight:600; text-transform:uppercase; letter-spacing:0.5px; margin-bottom:2px }
  .info-value { font-size:14px; color:#1a202c; font-weight:700 }
  .seats-info { margin-bottom:24px }
  .seats-header { display:flex; justify-content:space-between; align-items:center; margin-bottom:8px }
  .seats-text { font-size:13px; color:#4b5563; font-weight:600; display:flex; align-items:center; gap:6px }
  .seats-text svg { font-size:14px }
  .seats-status { font-size:12px; font-weight:700 }
  .seats-progress { height:8px; background:#e5e7eb; border-radius:10px; overflow:hidden }
  .seats-progress-bar { height:100%; transition:width 0.6s ease; border-radius:10px }
  .session-footer { display:flex; justify-content:space-between; align-items:center; padding-top:20px; border-top:2px solid #f3f4f6 }
  .price-label { font-size:12px; color:#9ca3af; font-weight:600; margin-bottom:4px }
  .price-value { font-size:20px; font-weight:900; color:#198754 }
  .btn-inscription { display:flex; align-items:center; gap:8px; padding:12px 28px; background:linear-gradient(135deg,#198754 0%,#20c997 100%); color:white; border:none; border-radius:10px; font-size:15px; font-weight:700; cursor:pointer; transition:all 0.3s ease; font-family:inherit; box-shadow:0 4px 12px rgba(25,135,84,0.3); text-decoration:none }
  .btn-inscription:hover { transform:translateY(-2px); box-shadow:0 8px 20px rgba(25,135,84,0.4) }

  /* No results */
  .no-results { text-align:center; padding:80px 32px; background:white; border-radius:24px; box-shadow:0 4px 20px rgba(0,0,0,0.06) }
  .no-results-icon { font-size:64px; color:#e5e7eb; margin-bottom:24px }
  .no-results h3 { font-size:28px; font-weight:700; color:#374151; margin-bottom:12px }
  .no-results p { font-size:16px; color:#6b7280; margin-bottom:32px }
  .btn-reset { padding:14px 32px; background:linear-gradient(135deg,#198754 0%,#20c997 100%); color:white; border:none; border-radius:12px; font-size:16px; font-weight:600; cursor:pointer; transition:all 0.3s ease; font-family:inherit }
  .btn-reset:hover { transform:translateY(-3px); box-shadow:0 10px 30px rgba(25,135,84,0.4) }

  /* Responsive */
  @media(max-width:1024px){ .hero-title{font-size:42px} .stats-grid{grid-template-columns:repeat(2,1fr)} .sessions-grid{grid-template-columns:repeat(auto-fill,minmax(320px,1fr))} }
  @media(max-width:768px){ .sessions-hero{padding:100px 0 60px;min-height:auto} .hero-title{font-size:36px} .hero-description{font-size:16px} .stats-grid{grid-template-columns:1fr;gap:16px} .filters-bar{flex-direction:column;align-items:stretch} .filters-left{flex-direction:column} .filter-group,.filter-select{width:100%} .sessions-grid{grid-template-columns:1fr} .session-info{grid-template-columns:1fr} .session-footer{flex-direction:column;gap:16px;align-items:stretch} .btn-inscription{width:100%;justify-content:center} }
  @media(max-width:640px){ .sessions-container{padding:0 16px} .hero-title{font-size:28px} .session-content{padding:20px} .session-title{font-size:18px} }
`;

export default DowSession;